import type { ReactNode } from "react";

import {
  BASE_ONBOARDING_TRAIL,
  OnboardingTrail,
  type OnboardingTrailStep,
} from "@/components/onboarding/OnboardingTrail";
import { OnboardingValueBlock } from "@/components/onboarding/OnboardingValueBlock";

interface OnboardingBrandColumnProps {
  /** Pulsing-dot eyebrow copy, e.g. "Vas a tardar 5 minutos". */
  eyebrow: ReactNode;
  /** Crimson italic quote. Wrap accent words in <span className="accent">. */
  quote: ReactNode;
  /** Current step (1-indexed). When provided, the trail renders. */
  currentStep?: number;
  /** Override the default 4-step base-onboarding trail. */
  trailSteps?: OnboardingTrailStep[];
  /** Rendered immediately below the trail (e.g. "Después · si seguís vos" after-card). */
  afterTrail?: ReactNode;
  /** Optional value block — head + bullets. Renders after `afterTrail`. */
  valueBlock?: { head: ReactNode; items: ReactNode[] };
  /** Free-form extra content (e.g. a custom dgi-feed). Renders last. */
  extra?: ReactNode;
  /** Alias of `extra` kept for back-compat (renders last after valueBlock/extra). */
  afterContent?: ReactNode;
}

export function OnboardingBrandColumn({
  eyebrow,
  quote,
  currentStep,
  trailSteps,
  afterTrail,
  valueBlock,
  extra,
  afterContent,
}: OnboardingBrandColumnProps): JSX.Element {
  const steps = trailSteps ?? BASE_ONBOARDING_TRAIL;
  return (
    <div className="fx-onb-brand-col">
      <div>
        <span className="fx-onb-brand-eyebrow">
          <span className="dot" />
          {eyebrow}
        </span>
        <p className="fx-onb-brand-quote">{quote}</p>
      </div>
      {currentStep !== undefined ? (
        <OnboardingTrail steps={steps} current={currentStep} />
      ) : null}
      {afterTrail}
      {valueBlock ? (
        <OnboardingValueBlock head={valueBlock.head} items={valueBlock.items} />
      ) : null}
      {extra}
      {afterContent}
    </div>
  );
}
